import { Stack, Typography, useTheme } from '@mui/material';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Label,
    Line,
    LineChart,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { _ } from '/@/locale';
import { type AxisScale, co2BalanceScale, round, type SeasonResult, seasonLabel, soilCarbonScale } from '../utils';

const CHART_HEIGHT = 260;
const CHART_MARGIN = { top: 30, bottom: 0, left: 8, right: 24 };
// Keep the first and last campaign labels (e.g. "Carbono Inicial") inside the chart.
const X_AXIS_PADDING = { left: 40, right: 40 };

const yAxisProps = ({ domain, ticks, decimals }: AxisScale) => ({
    type: 'number' as const,
    domain,
    ticks,
    interval: 0 as const,
    tickFormatter: (value: number) => value.toFixed(decimals),
    width: 48,
});

interface ChartProps {
    calculationResult: SeasonResult[];
}

function ChartSection({ title, children }: { title: string; children: React.ReactElement }) {
    return (
        <Stack spacing={1}>
            <Typography variant='subtitle1' color='primary' fontWeight={500}>
                {title}
            </Typography>
            <ResponsiveContainer width='100%' height={CHART_HEIGHT}>
                {children}
            </ResponsiveContainer>
        </Stack>
    );
}

function LineGraphic({ calculationResult }: ChartProps) {
    const theme = useTheme();
    const data = calculationResult.map((res) => ({
        campaign: seasonLabel(res.season),
        SIM: round(res.co.simulated, 2),
    }));
    const scale = soilCarbonScale(data.map((point) => point.SIM));

    return (
        <ChartSection title={_('Carbono orgánico en el suelo (SIM)')}>
            <LineChart data={data} margin={CHART_MARGIN}>
                <CartesianGrid strokeDasharray='3 3' />
                <XAxis dataKey='campaign' padding={X_AXIS_PADDING} />
                <YAxis {...yAxisProps(scale)}>
                    <Label value='SIM' offset={15} position='top' />
                </YAxis>
                <Tooltip />
                <Line type='monotone' dataKey='SIM' stroke={theme.palette.primary.main} activeDot={{ r: 8 }} />
            </LineChart>
        </ChartSection>
    );
}

function BarGraphic({ calculationResult }: ChartProps) {
    const theme = useTheme();
    // The first campaign is the initial carbon, with no balance yet.
    const data = calculationResult.slice(1).map((res) => ({
        campaign: seasonLabel(res.season),
        CO2: round(res.co.fixed, 2),
    }));

    return (
        <ChartSection title={_('CO2 fijado')}>
            <BarChart data={data} margin={CHART_MARGIN} maxBarSize={50}>
                <CartesianGrid strokeDasharray='3 3' />
                <XAxis dataKey='campaign' />
                <YAxis {...yAxisProps(co2BalanceScale(data.map((point) => point.CO2)))}>
                    <Label value='CO2' offset={15} position='top' />
                </YAxis>
                <Tooltip />
                <ReferenceLine y={0} stroke={theme.palette.primary.main} />
                <Bar dataKey='CO2'>
                    {data.map((point) => (
                        <Cell
                            key={point.campaign}
                            fill={point.CO2 > 0 ? theme.palette.success.main : theme.palette.error.main}
                        />
                    ))}
                </Bar>
            </BarChart>
        </ChartSection>
    );
}

export default function CarbonCharts({
    calculationResult,
    sideBySide = false,
}: ChartProps & { /** Charts in two columns from `md` up, for the expanded view. */ sideBySide?: boolean }) {
    if (calculationResult.length < 2) {
        return (
            <Typography variant='body2' color='primary.mui.inactive'>
                {_('Cargá al menos dos campañas para ver los gráficos.')}
            </Typography>
        );
    }

    return (
        <Stack
            direction={sideBySide ? { xs: 'column', md: 'row' } : 'column'}
            gap={3}
            sx={{ '& > *': { flex: 1, minWidth: 0 } }}
        >
            <LineGraphic calculationResult={calculationResult} />
            <BarGraphic calculationResult={calculationResult} />
        </Stack>
    );
}
